// Fila de mensagens do usuário digitadas DURANTE uma geração: em vez de
// bloquear o envio (ou cancelar o stream), a mensagem espera o turno acabar e
// é enviada sozinha. A fila é por chat — trocar de conversa não a perde.

export interface QueuedMessage {
  id: string;
  chatId: string;
  content: string;
  files: File[] | null;
}

let nextId = 0;
export function newQueuedMessage(chatId: string, content: string, files: File[] | null): QueuedMessage {
  nextId += 1;
  return { id: `q${Date.now()}-${nextId}`, chatId, content, files: files && files.length > 0 ? files : null };
}

export function queuedForChat(queue: QueuedMessage[], chatId: string): QueuedMessage[] {
  return queue.filter((m) => m.chatId === chatId);
}

export function withoutQueued(queue: QueuedMessage[], id: string): QueuedMessage[] {
  return queue.filter((m) => m.id !== id);
}

export interface DispatchState {
  chatId: string;
  loadingGeneration: boolean;
  cancellingBackground: boolean;
  chatErrorMessage: string;
  comparisonMode: boolean;
  isRecording: boolean;
  sendingAudio: boolean;
  isEditing: boolean;
  // Histórico da conversa ainda carregando: o envio é recusado até ele chegar
  // (o esforço de raciocínio da conversa vem com ele), então nem tenta.
  loadingHistory?: boolean;
  // Após um "Parar" explícito a fila fica em espera: disparar a próxima
  // mensagem sozinha contrariaria quem acabou de interromper o modelo.
  held: boolean;
}

// A próxima mensagem da fila só sai quando o chat está ocioso e nada que
// compete pelo mesmo turno (erro, comparação, edição, áudio) está ativo.
export function canDispatchQueued(s: DispatchState): boolean {
  return !!s.chatId
    && !s.loadingGeneration
    && !s.cancellingBackground
    && !s.chatErrorMessage
    && !s.comparisonMode
    && !s.isRecording
    && !s.sendingAudio
    && !s.isEditing
    && !s.loadingHistory
    && !s.held;
}

// Despacho de uma mensagem da fila: só sai da fila quando o envio é aceito;
// se o envio não pôde começar (gravação de áudio, erro de autenticação, etc.)
// ela volta para a frente da fila em vez de sumir com texto e anexos.
export interface DispatchDeps {
  canSend: boolean;
  remove: (id: string) => void;
  restore: (item: QueuedMessage) => void;
  submit: (content: string, files: File[] | null) => Promise<boolean> | boolean | void;
}

export async function dispatchQueuedMessage(item: QueuedMessage, deps: DispatchDeps): Promise<boolean> {
  if (!deps.canSend) return false;
  deps.remove(item.id);
  let ok = false;
  try {
    ok = (await deps.submit(item.content, item.files)) === true;
  } catch {
    ok = false;
  }
  if (!ok) deps.restore(item);
  return ok;
}

// "Editar" devolve a mensagem à caixa de texto sem engolir um rascunho que o
// usuário já tenha começado: os textos se juntam e os anexos se somam.
export function mergeIntoComposer(
  item: QueuedMessage,
  draft: string,
  draftFiles: File[] | null,
): { content: string; files: File[] | null } {
  const content = draft.trim().length > 0 ? `${draft.replace(/\s+$/, '')}\n${item.content}` : item.content;
  const files = [...(draftFiles || []), ...(item.files || [])];
  return { content, files: files.length > 0 ? files : null };
}
